import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/lib/api";
import { Question, X, PaperPlaneRight, Sparkle, ArrowUpRight, ChatCircleDots } from "@phosphor-icons/react";

const SUPPORT_EMAIL = "hello@workwithlottie.com";

const STARTERS = [
  "How do I connect Xero?",
  "How do I schedule a prepayment?",
  "How do I reconcile to Xero?",
  "How do I close the month?",
];

function getSessionId() {
  let sid = localStorage.getItem("assistant_session_id");
  if (!sid) {
    sid = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
    localStorage.setItem("assistant_session_id", sid);
  }
  return sid;
}

// Lightweight text renderer: paragraphs + "- "/"1." bullets, no external markdown dep.
function RichText({ text }) {
  const lines = (text || "").replace(/\\n/g, "\n").split("\n");
  return (
    <div className="space-y-1.5">
      {lines.map((ln, i) => {
        const t = ln.trim();
        if (!t) return null;
        const bullet = /^(-|•)\s+/.test(t);
        const numbered = /^\d+[.)]\s+/.test(t);
        if (bullet) return <div key={i} className="flex gap-2"><span className="text-orangeink">•</span><span>{t.replace(/^(-|•)\s+/, "")}</span></div>;
        if (numbered) return <div key={i} className="flex gap-2"><span className="text-orangeink font-semibold">{t.match(/^\d+/)[0]}.</span><span>{t.replace(/^\d+[.)]\s+/, "")}</span></div>;
        return <p key={i}>{t}</p>;
      })}
    </div>
  );
}

export function AssistantChat({ pathname, helpFocus }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [hint, setHint] = useState(() => !localStorage.getItem("assistant_hint_seen"));
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const dismissHint = () => { localStorage.setItem("assistant_hint_seen", "1"); setHint(false); };

  const openChat = () => {
    dismissHint();
    setOpen(true);
    if (messages.length === 0) {
      setMessages([{ role: "assistant", answer: "Hi, I'm Lottie's assistant. Ask me how to do anything in the app — from connecting Xero to closing the month.", links: [] }]);
    }
    setTimeout(() => inputRef.current?.focus(), 150);
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    // Append the user message + an empty assistant bubble we'll stream into.
    let assistantIdx;
    setMessages((m) => {
      const next = [...m, { role: "user", answer: q }, { role: "assistant", answer: "", links: [] }];
      assistantIdx = next.length - 1;
      return next;
    });
    setBusy(true);

    const setAssistant = (patch) =>
      setMessages((m) => m.map((msg, i) => (i === assistantIdx ? { ...msg, ...patch } : msg)));

    try {
      const token = localStorage.getItem("accrued_token");
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: "include",
        body: JSON.stringify({ message: q, session_id: getSessionId(), page_route: pathname || "" }),
      });

      if (!res.ok || !res.body) {
        let detail = "I'm having trouble right now. Please try again, or email " + SUPPORT_EMAIL + ".";
        try { const j = await res.json(); if (j.detail) detail = j.detail; } catch { /* noop */ }
        setAssistant({ answer: detail, links: [] });
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let evt;
          try { evt = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (evt.type === "session") localStorage.setItem("assistant_session_id", evt.session_id);
          else if (evt.type === "delta") setAssistant({ answer: evt.text });
          else if (evt.type === "done") setAssistant({ answer: evt.answer, links: evt.links || [] });
          else if (evt.type === "error") setAssistant({ answer: evt.detail, links: [] });
        }
      }
    } catch {
      setAssistant({ answer: "I'm having trouble right now. Please try again, or email " + SUPPORT_EMAIL + "." });
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const goto = (to) => { setOpen(false); nav(to); };

  return (
    <>
      {/* Launcher */}
      {!open && (
        <div className="fixed bottom-6 right-6 z-40 flex items-center gap-3">
          {hint && (
            <div data-testid="assistant-hint"
              className="fade-up flex items-center gap-2 bg-white rounded-xl shadow-card-hover border border-slate-200 pl-3 pr-2 py-2 max-w-[240px]"
              style={{ boxShadow: "0 8px 24px rgba(11,26,47,0.18)" }}>
              <span className="text-xs text-navy/80 leading-snug">
                <span className="font-semibold text-navy">Ask Lottie</span> — get help for this page.
              </span>
              <button onClick={dismissHint} data-testid="assistant-hint-dismiss" aria-label="Dismiss"
                className="shrink-0 w-6 h-6 rounded-lg flex items-center justify-center text-slate-600 hover:bg-slate-100 hover:text-navy transition-colors">
                <X size={14} weight="bold" />
              </button>
            </div>
          )}
          <div className="relative">
            {hint && <span className="absolute inset-0 rounded-full bg-orange/40 animate-ping pointer-events-none" aria-hidden="true" />}
            <button onClick={openChat} data-testid="assistant-launcher" title="Ask Lottie"
              className={`relative w-14 h-14 rounded-full text-white flex items-center justify-center transition-colors ${hint ? "bg-orange hover:bg-orangedark" : "bg-navy hover:bg-navy/90"}`}
              style={{ boxShadow: "0 8px 24px rgba(11,26,47,0.28)" }}>
              <ChatCircleDots size={26} weight="fill" />
            </button>
          </div>
        </div>
      )}

      {/* Panel */}
      {open && (
        <div data-testid="assistant-panel"
          className="fixed bottom-6 right-6 z-50 w-[min(400px,calc(100vw-2rem))] h-[min(600px,calc(100vh-3rem))] bg-white rounded-2xl border border-slate-200 flex flex-col overflow-hidden fade-up"
          style={{ boxShadow: "0 20px 50px rgba(11,26,47,0.30)" }}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-navy text-white shrink-0">
            <div className="flex items-center gap-2.5">
              <span className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center">
                <Sparkle size={17} weight="fill" style={{ color: "#F37A24" }} />
              </span>
              <div>
                <div className="font-heading font-semibold text-sm leading-none">Ask Lottie</div>
                <div className="text-[0.68rem] text-white/60 mt-0.5">Answers from the help guides</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="assistant-close"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white/70 hover:bg-white/10 hover:text-white transition-colors">
              <X size={18} weight="bold" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-slate-50/60">
            {messages.map((m, i) => (
              (m.role === "assistant" && !m.answer) ? null : (
              <div key={i} data-testid={`assistant-msg-${m.role}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  m.role === "user" ? "bg-navy text-white rounded-br-sm" : "bg-white border border-slate-200 text-navy/85 rounded-bl-sm"}`}>
                  <RichText text={m.answer} />
                  {m.links?.length > 0 && (
                    <div className="mt-3 flex flex-col gap-1.5">
                      {m.links.map((l, li) => (
                        <button key={li} onClick={() => goto(l.to)} data-testid="assistant-link"
                          className="group inline-flex items-center justify-between gap-2 text-xs font-semibold text-orangeink bg-orange/[0.07] hover:bg-orange/[0.14] border border-orange/25 rounded-lg px-3 py-2 transition-colors">
                          <span>Take me to: {l.label}</span>
                          <ArrowUpRight size={14} weight="bold" className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              )
            ))}
            {busy && !(messages[messages.length - 1]?.role === "assistant" && messages[messages.length - 1]?.answer) && (
              <div className="flex justify-start" data-testid="assistant-typing">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}

            {/* Starters (only before the user has asked anything) */}
            {messages.filter((m) => m.role === "user").length === 0 && !busy && (
              <div className="flex flex-wrap gap-2 pt-1" data-testid="assistant-starters">
                {STARTERS.map((s) => (
                  <button key={s} onClick={() => send(s)}
                    className="text-xs text-navy/70 bg-white border border-slate-200 rounded-full px-3 py-1.5 hover:border-orange hover:text-orangeink transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer / input */}
          <div className="border-t border-slate-100 bg-white shrink-0">
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2 p-3">
              <textarea ref={inputRef} value={input} rows={1} data-testid="assistant-input"
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask how to do something…"
                className="flex-1 resize-none max-h-24 rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-navy placeholder:text-slate-600 outline-none focus:border-orange transition-colors" />
              <button type="submit" disabled={busy || !input.trim()} data-testid="assistant-send"
                className="shrink-0 w-10 h-10 rounded-xl bg-orange text-white flex items-center justify-center hover:bg-orangedark transition-colors disabled:opacity-40">
                <PaperPlaneRight size={18} weight="fill" />
              </button>
            </form>
            <div className="flex items-center justify-between px-4 pb-2.5 -mt-1">
              <button onClick={() => goto(helpFocus ? `/help?focus=${encodeURIComponent(helpFocus)}` : "/help")}
                data-testid="assistant-browse-help" className="text-[0.68rem] text-navy/70 hover:text-orangeink transition-colors inline-flex items-center gap-1">
                <Question size={12} weight="bold" /> Browse the full Help Centre
              </button>
              <a href={`mailto:${SUPPORT_EMAIL}?subject=WorkWithLottie%20support`} data-testid="assistant-email-support"
                className="text-[0.68rem] text-navy/70 hover:text-orangeink transition-colors">Email support</a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
